import { useContext } from 'react'
import { observer } from 'mobx-react-lite'

import type { components } from '../../../constants/schema'

import { calculateAge } from '../../../utils'

import { StoresContext } from '../../../provider/StoreProvider'
import { ModalContext } from '../../../provider/modalProvider'

import { useNavigate } from '../../../hooks/useNavigate'
import { useAlert } from '../../../hooks'

import { Button } from '../../../components/button'
import { Text } from '../../../components/text'
import type { ButtonProps } from '../../../components/button/Button'
import { Footer } from '../../../components/footer'
import { Spinner } from '../../../components/spinner/Spinner'
import { AddressModal } from '../modals/AddressModal'
import { SuitableNoticeModal } from '../modals/SuitableNoticeModal'
import { VerificationNoticeModal } from '../modals/VerificationNoticeModal'
import { CreateAccountModal } from '../../settings/modal'
import FooterNotify from './FooterNotify'
import type { FooterNotifyProps } from './FooterNotify'

interface FooterButtonProps {
  subscriptionStatus?: components['schemas']['ProductDetailDto']['subscriptionStatus']
  subscriptionStatusLabel?: components['schemas']['ProductDetailDto']['subscriptionStatusLabel']
}

type DetailFooterProps = FooterButtonProps & FooterNotifyProps

export default function DetailFooter({
  activeProductSale,
  overallStatus,
  subscriptionStatus,
  subscriptionStatusLabel,
}: DetailFooterProps) {
  return (
    <Footer background="fill">
      <FooterNotify activeProductSale={activeProductSale} overallStatus={overallStatus} />
      <FooterButton subscriptionStatus={subscriptionStatus} subscriptionStatusLabel={subscriptionStatusLabel} />
    </Footer>
  )
}

const FooterButton = observer(({ subscriptionStatus, subscriptionStatusLabel }: DetailFooterProps) => {
  const {
    authStore: { userInfo },
    myStore: { assetHoldPagingStore },
  } = useContext(StoresContext)
  const { openModal } = useContext(ModalContext)

  const navigate = useNavigate()
  const openAlert = useAlert()

  const id = Number(location.pathname.split('/').pop())

  const goMyAssetProduct = async () => {
    await assetHoldPagingStore.load(undefined, true)

    const assetId = assetHoldPagingStore.content?.list.find(({ productId }) => productId === id)?.id
    if (typeof assetId === 'undefined') return

    navigate(`/myPage/asset/${assetId}`)
  }

  const order = () => {
    if (typeof userInfo?.birthday === 'undefined' || calculateAge(userInfo?.birthday) < 19) {
      openAlert({
        content: (
          <Text size={16} weight={500} lineHeight={24} $textAlign="center">
            만19세 이상부터 구매가 가능해요.
            <br />
            자세한 내용은 FAQ를 참고해주세요.
            <br />
          </Text>
        ),
      })
      return
    }

    navigate(`/market/product/${id}/order`)
  }

  const buttonProps: () => ButtonProps = () => {
    switch (subscriptionStatus) {
      case 'TO_BE_RELEASED': //'청약 예정'
      case 'SUBSCRIPTION_CLOSED': //'청약 종료됨'
      case 'UNKNOWN': //'오류'
        return { disabled: true }
      case 'NEED_LOGIN': //'로그인 후 이용이 가능해요'
        return { type: 'dark', href: '/signIn' }
      case 'NEED_IDENTIFY': //'본인인증이 필요합니다'
        return {
          type: 'dark',
          onClick: () => openModal({ id: 'VERIFICATION_NOTICE', Component: VerificationNoticeModal }),
        }
      case 'NEED_ADDRESS': //'주소입력이 필요합니다'
        return { type: 'dark', onClick: () => openModal({ id: 'EMPTY_ADDRESS', Component: AddressModal }) }
      case 'NEED_DEPOSIT_ACCOUNT': //'입출금계좌를 개설해주세요'
        return { type: 'dark', onClick: () => openModal({ id: 'CREATE_ACCOUNT', Component: CreateAccountModal }) }
      case 'NEED_SUITABILITY_TEST': //'투자적합성 테스트를 완료하세요'
        return { type: 'dark', onClick: () => openModal({ id: 'SUITABLE_NOTICE', Component: SuitableNoticeModal }) }
      case 'SUBSCRIPTION_AVAILABLE': //'청약 신청하기'
        return { type: 'primary', onClick: order }
      case 'HAS_SUBSCRIPTION': //'청약 신청 내역 확인하기'
        return { type: 'primary', onClick: goMyAssetProduct }
      default:
        return { disabled: true }
    }
  }

  return (
    <Button {...buttonProps()} size="large" block={true}>
      {subscriptionStatusLabel ?? <Spinner />}
    </Button>
  )
})
